import React, { useState } from 'react';
import Icon from './Icon';

/* ── SectionHeader ───────────────────────────────────────────────────
   Page-level section heading ("Our Services", "Specialties", …). */
export const SectionHeader = ({ title, subtitle, meta, onViewAll, viewAllLabel = 'View all' }) => (
  <div className="ds-section-head">
    <div>
      <h2 className="ds-section-title">{title}</h2>
      {subtitle && <p className="ds-section-sub">{subtitle}</p>}
    </div>
    {meta != null && meta !== false && <span className="ds-section-meta">{meta}</span>}
    {onViewAll && (
      <button type="button" className="ds-view-all" onClick={onViewAll}>
        {viewAllLabel} <Icon name="arrowRight" size={13} />
      </button>
    )}
  </div>
);

/* ── PanelHeader ─────────────────────────────────────────────────────
   Header row inside a .ds-panel card. */
export const PanelHeader = ({ icon, title, count, onViewAll, viewAllLabel = 'View all' }) => (
  <div className="ds-panel-head">
    <h3 className="ds-panel-title">
      {icon && <Icon name={icon} size={18} />}
      {title}
      {count != null && <span className="ds-count">{count}</span>}
    </h3>
    {onViewAll && (
      <button type="button" className="ds-view-all" onClick={onViewAll}>
        {viewAllLabel} <Icon name="arrowRight" size={13} />
      </button>
    )}
  </div>
);

/* ── Pill ────────────────────────────────────────────────────────────
   Status / role badge. `tone` maps to the .tone-* palettes. */
export const Pill = ({ tone = 'indigo', children, style }) => (
  <span className={`ds-pill tone-${tone}`} style={style}>{children}</span>
);

/* ── StatCard ────────────────────────────────────────────────────────
   KPI tile. Renders as a button when `onClick` is given. */
export const StatCard = ({ icon, tone = 'indigo', value, label, loading, onClick }) => {
  const Tag = onClick ? 'button' : 'div';
  return (
    <Tag type={onClick ? 'button' : undefined} className={`ds-stat tone-${tone}`} onClick={onClick}>
      <div className="ds-stat-top">
        <span className={`ds-icon-box tone-${tone}`}><Icon name={icon} size={20} /></span>
        {onClick && <span className="ds-stat-arrow"><Icon name="arrowUpRight" size={13} strokeWidth={2.4} /></span>}
      </div>
      <div>
        <div className="ds-stat-value">
          {loading ? <span className="skeleton" style={{ display: 'inline-block', width: 44, height: 22 }} /> : (value ?? '—')}
        </div>
        <div className="ds-stat-label">{label}</div>
      </div>
    </Tag>
  );
};

/* ── QuickAction ─────────────────────────────────────────────────── */
export const QuickAction = ({ icon, tone = 'indigo', label, onClick }) => (
  <button type="button" className="ds-quick" onClick={onClick}>
    <span className={`ds-icon-box tone-${tone}`}><Icon name={icon} size={19} /></span>
    {label}
    <Icon name="chevronRight" size={16} className="ds-quick-arrow" />
  </button>
);

/* ── SearchBar ───────────────────────────────────────────────────────
   Rounded search input with a submit button. Uncontrolled by default;
   calls onSubmit(trimmedValue). */
export const SearchBar = ({ placeholder, onSubmit, defaultValue = '', ariaLabel = 'Search' }) => {
  const [value, setValue] = useState(defaultValue);
  const submit = (e) => {
    e.preventDefault();
    onSubmit && onSubmit(value.trim());
  };
  return (
    <form className="ds-search" onSubmit={submit} role="search">
      <Icon name="search" size={17} />
      <input
        type="text" value={value} aria-label={ariaLabel}
        placeholder={placeholder}
        onChange={(e) => setValue(e.target.value)}
      />
      <button type="submit" className="ds-search-btn" aria-label={ariaLabel}>
        <Icon name="search" size={17} strokeWidth={2.4} />
      </button>
    </form>
  );
};

/* ── Rating ──────────────────────────────────────────────────────── */
export const Rating = ({ rating, reviewCount }) => {
  const r = parseFloat(rating) || 0;
  if (!r && !reviewCount) return null;
  return (
    <div className="ds-doc-rating">
      <Icon name="star" size={13} fill="currentColor" strokeWidth={1} />
      <strong>{r.toFixed(1)}</strong>
      {reviewCount != null && (
        <span>({reviewCount} {reviewCount === 1 ? 'review' : 'reviews'})</span>
      )}
    </div>
  );
};
